import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAssetProblemReport, exportAssetProblemReport } from '../assetProblemReportService';
import { formatDateToYyyyMmDd } from '../../../utils/formatDate';

const toggleInArray = (arr, value) => (arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);

const parseListParam = (value) => (value ? value.split(',').filter(Boolean) : []);
const parseDateParam = (value) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
};

// level is derived from the multi-select filters: markets selected → 'team', states selected → 'market', else 'state'.
export function useAssetProblemReport() {
    const [searchParams, setSearchParams] = useSearchParams();
    const didInit = useRef(false);

    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);

    const [selectedStates, setSelectedStates] = useState(() => parseListParam(searchParams.get('states')));
    const [selectedMarkets, setSelectedMarkets] = useState(() => parseListParam(searchParams.get('markets')).map(Number));
    const [selectedTeamIds, setSelectedTeamIds] = useState(() => parseListParam(searchParams.get('teams')).map(Number));
    const [startDate, setStartDate] = useState(() => parseDateParam(searchParams.get('start')));
    const [endDate, setEndDate] = useState(() => parseDateParam(searchParams.get('end')));

    const level = selectedMarkets.length > 0 ? 'team' : selectedStates.length > 0 ? 'market' : 'state';
    const filters = useMemo(() => data?.filters ?? { states: [], markets: [], teams: [] }, [data]);

    const buildParams = useCallback(() => {
        const params = { level };
        if (startDate) params.start = formatDateToYyyyMmDd(startDate);
        if (endDate) params.end = formatDateToYyyyMmDd(endDate);
        if (selectedStates.length > 0) params.state = selectedStates;
        if (selectedMarkets.length > 0) params.market_id = selectedMarkets;
        if (selectedTeamIds.length > 0) params.team_id = selectedTeamIds;
        return params;
    }, [level, startDate, endDate, selectedStates, selectedMarkets, selectedTeamIds]);

    const fetchReport = useCallback(async () => {
        setLoaded(false);
        setError('');
        try {
            const result = await getAssetProblemReport(buildParams());
            setData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }, [buildParams]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // Reflects the active filters in the URL so a filtered view is shareable/reloadable.
    // Skips the very first render so mount doesn't blow away params another hook/effect set.
    useEffect(() => {
        if (!didInit.current) {
            didInit.current = true;
            return;
        }
        const next = new URLSearchParams();
        if (selectedStates.length > 0) next.set('states', selectedStates.join(','));
        if (selectedMarkets.length > 0) next.set('markets', selectedMarkets.join(','));
        if (selectedTeamIds.length > 0) next.set('teams', selectedTeamIds.join(','));
        if (startDate) next.set('start', formatDateToYyyyMmDd(startDate));
        if (endDate) next.set('end', formatDateToYyyyMmDd(endDate));
        setSearchParams(next, { replace: true });
    }, [selectedStates, selectedMarkets, selectedTeamIds, startDate, endDate, setSearchParams]);

    // Clears markets/teams whenever the state selection changes to avoid stale, out-of-scope selections.
    const handleToggleState = useCallback((stateCode) => {
        setSelectedStates((prev) => toggleInArray(prev, stateCode));
        setSelectedMarkets([]);
        setSelectedTeamIds([]);
    }, []);

    const handleToggleMarket = useCallback((marketId) => {
        setSelectedMarkets((prev) => toggleInArray(prev, marketId));
        setSelectedTeamIds([]);
    }, []);

    // Dropdown onChange handlers — replace the full selection rather than toggling a single value.
    const handleStatesChange = useCallback((values) => {
        setSelectedStates(values);
        setSelectedMarkets([]);
        setSelectedTeamIds([]);
    }, []);

    const handleMarketsChange = useCallback((values) => {
        setSelectedMarkets(values);
        setSelectedTeamIds([]);
    }, []);

    // Picking a team only scopes the main report request (via `team_id` in buildParams) —
    // it doesn't touch the State/Market filters.
    const handleTeamsChange = useCallback((values) => {
        setSelectedTeamIds(values);
    }, []);

    const handleClearFilters = useCallback(() => {
        setSelectedStates([]);
        setSelectedMarkets([]);
        setSelectedTeamIds([]);
    }, []);

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            await exportAssetProblemReport(buildParams());
        } catch (err) {
            setError(err.message);
        } finally {
            setExporting(false);
        }
    }, [buildParams]);

    const groups = useMemo(() => data?.groups ?? [], [data]);
    const overall = useMemo(() => data?.overall ?? null, [data]);

    return {
        data,
        groups,
        overall,
        filters,
        loaded,
        error,
        exporting,
        level,
        selectedStates,
        selectedMarkets,
        selectedTeamIds,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        handleToggleState,
        handleToggleMarket,
        handleStatesChange,
        handleMarketsChange,
        handleTeamsChange,
        handleClearFilters,
        handleExport,
        buildParams,
    };
}

